<template>
  <div :class="$style.wrapper">
    <div>
      <div :class="$style.heading">対応状況</div>
      <div :class="$style.statuses">
        <p v-if="isSelectedAllChatRoomStatus">すべて</p>
        <template v-else v-for="status in modelValue.statuses" :key="status">
          <ChatRoomStatusItem :status="status" />
        </template>
      </div>
    </div>
    <div>
      <div :class="$style.heading">チャット状況</div>
      <div :class="$style.statuses">
        <p v-if="isSelectedAllChatMessageStatus">すべて</p>
        <p
          v-else
          v-for="status in modelValue.chatMessageStatuses"
          :key="status"
        >
          {{ chatMessageStatusString[status] }}
        </p>
      </div>
    </div>
    <div>
      <div :class="$style.heading">メモ</div>
      <div :class="$style.statuses">
        <p v-if="isSelectedAllChatRoomMemoStatus">すべて</p>
        <p v-else v-for="status in modelValue.memoStatuses" :key="status">
          {{ chatRoomMemoStatusString[status] }}
        </p>
      </div>
    </div>
    <div>
      <div :class="$style.heading">最新メッセージの日時</div>
      <div :class="$style.statuses">
        <p>{{ lastMessageSentAtString }}</p>
      </div>
    </div>
    <FlatButton
      :class="$style.wrapper__button"
      @click="openChatRoomStatusFilterModal"
      size="small"
      variant="primary"
    >
      リストの絞り込み
    </FlatButton>
    <ChatRoomStatusFilterModal>
      <FilterModal
        :modelValue="modelValue"
        @update:modelValue="onUpdate"
        @close="onCloseModal"
      />
    </ChatRoomStatusFilterModal>
  </div>
</template>

<script lang="ts" setup>
import { allChatRoomStatuses } from '@/models/chatRoom/chatRoomStatus'
import FilterModal from './FilterModal.vue'
import ChatRoomStatusItem from './ChatRoomStatusItem.vue'
import { computed } from 'vue'
import type { ChatRoomFilterInput } from '@/types/schema.generated'
import { useModal } from '@/hooks/useModal'
import {
  allChatMessageStatuses,
  chatMessageStatusString,
} from '@/models/chatRoom/chatMessageStatus'
import { fromTimeString } from '@/libs/utilities/gql-scalar'
import { format } from 'date-fns'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  allChatRoomMemoStatuses,
  chatRoomMemoStatusString,
} from '@/models/chatRoom/chatRoomMemoStatus'

const props = defineProps<{
  modelValue: ChatRoomFilterInput
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', selectedStatuses: ChatRoomFilterInput): void
}>()

const {
  ChatRoomStatusFilterModal,
  closeChatRoomStatusFilterModal,
  openChatRoomStatusFilterModal,
} = useModal('chatRoomStatusFilter')

const isSelectedAllChatRoomStatus = computed(() => {
  if (props.modelValue.statuses == null) {
    return true
  }
  return props.modelValue.statuses.length === allChatRoomStatuses.length
})

const isSelectedAllChatMessageStatus = computed(() => {
  if (props.modelValue.chatMessageStatuses == null) {
    return true
  }
  return (
    props.modelValue.chatMessageStatuses.length ===
    allChatMessageStatuses.length
  )
})

const isSelectedAllChatRoomMemoStatus = computed(() => {
  if (props.modelValue.memoStatuses == null) {
    return true
  }
  return props.modelValue.memoStatuses.length === allChatRoomMemoStatuses.length
})

const lastMessageSentAtString = computed(() => {
  const sentAt = props.modelValue.lastMessageSentAt
  if (sentAt == null || (sentAt.startedAt == null && sentAt.endedAt == null)) {
    return 'すべて'
  }
  const formatMaybeTimeString = (
    timeString: TimeString | null | undefined
  ): string | null =>
    timeString != null
      ? format(fromTimeString(timeString), 'yyyy/MM/dd HH:mm')
      : null
  return [
    formatMaybeTimeString(sentAt?.startedAt),
    '〜',
    formatMaybeTimeString(sentAt?.endedAt),
  ]
    .filter(chunk => chunk != null)
    .join(' ')
})

const onUpdate = (input: ChatRoomFilterInput) => {
  emit('update:modelValue', input)
  closeChatRoomStatusFilterModal()
}

const onCloseModal = () => {
  closeChatRoomStatusFilterModal()
}
</script>

<style lang="scss" module>
.wrapper {
  > *:nth-child(n + 2) {
    margin-top: 8px;
  }

  &__button {
    width: 100%;
    margin-top: 16px;
  }
}

.heading {
  font-size: 12px;
  font-weight: bold;
}

.statuses {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}
</style>
